import React, { useState } from "react";
import { MainLayout } from "@/components/layout/MainLayout";
import { Helmet } from "react-helmet";
import { FileText, Cookie, Scale, Code, ChevronDown, ChevronUp, ReceiptText } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { useTranslation } from "@/hooks/useTranslation";

// Open source libraries used in the project
const OPEN_SOURCE_LIBRARIES = [
  { name: "React", version: "18.3.1", license: "MIT", url: "https://reactjs.org/" },
  { name: "React Router", version: "6.30.1", license: "MIT", url: "https://reactrouter.com/" },
  { name: "Tailwind CSS", version: "3.x", license: "MIT", url: "https://tailwindcss.com/" },
  { name: "Framer Motion", version: "12.23.25", license: "MIT", url: "https://www.framer.com/motion/" },
  { name: "Lucide React", version: "0.462.0", license: "ISC", url: "https://lucide.dev/" },
  { name: "Radix UI", version: "Various", license: "MIT", url: "https://www.radix-ui.com/" },
  { name: "TanStack Query", version: "5.83.0", license: "MIT", url: "https://tanstack.com/query" },
  { name: "date-fns", version: "3.6.0", license: "MIT", url: "https://date-fns.org/" },
  { name: "Recharts", version: "2.15.4", license: "MIT", url: "https://recharts.org/" },
  { name: "Zod", version: "3.25.76", license: "MIT", url: "https://zod.dev/" },
  { name: "React Hook Form", version: "7.61.1", license: "MIT", url: "https://react-hook-form.com/" },
  { name: "Sonner", version: "1.7.4", license: "MIT", url: "https://sonner.emilkowal.ski/" },
  { name: "Vaul", version: "0.9.9", license: "MIT", url: "https://vaul.emilkowal.ski/" },
  { name: "cmdk", version: "1.1.1", license: "MIT", url: "https://cmdk.paco.me/" },
  { name: "Embla Carousel", version: "8.6.0", license: "MIT", url: "https://www.embla-carousel.com/" },
  { name: "HLS.js", version: "1.6.15", license: "Apache-2.0", url: "https://github.com/video-dev/hls.js/" },
  { name: "MediaPipe Tasks Vision", version: "0.10.15", license: "Apache-2.0", url: "https://developers.google.com/mediapipe" },
  { name: "Supabase JS", version: "2.86.0", license: "MIT", url: "https://supabase.com/" },
  { name: "Class Variance Authority", version: "0.7.1", license: "Apache-2.0", url: "https://cva.style/" },
  { name: "clsx", version: "2.1.1", license: "MIT", url: "https://github.com/lukeed/clsx" },
  { name: "tailwind-merge", version: "2.6.0", license: "MIT", url: "https://github.com/dcastil/tailwind-merge" },
  { name: "React Day Picker", version: "8.10.1", license: "MIT", url: "https://react-day-picker.js.org/" },
  { name: "React Resizable Panels", version: "2.1.9", license: "MIT", url: "https://react-resizable-panels.vercel.app/" },
  { name: "input-otp", version: "1.4.2", license: "MIT", url: "https://input-otp.rodz.dev/" },
  { name: "next-themes", version: "0.3.0", license: "MIT", url: "https://github.com/pacocoursey/next-themes" },
  { name: "React Helmet", version: "6.1.0", license: "MIT", url: "https://github.com/nfl/react-helmet" },
];

// Cookies used in the application
const COOKIES_INFO = [
  {
    categories: { en: "Essential", he: "חיוניים", ar: "أساسية", ru: "Основные" },
    descriptions: { en: "Cookies required for the app to function properly", he: "עוגיות הנדרשות לפעולה תקינה של האפליקציה", ar: "ملفات تعريف الارتباط المطلوبة لعمل التطبيق بشكل صحيح", ru: "Файлы cookie, необходимые для работы приложения" },
    cookies: [
      { name: "user-settings", purposes: { en: "Stores user preferences and settings", he: "שמירת העדפות והגדרות משתמש", ar: "يحفظ تفضيلات وإعدادات المستخدم", ru: "Сохраняет настройки пользователя" } },
      { name: "cookie-consent", purposes: { en: "Remembers cookie consent choices", he: "שמירת הסכמה לעוגיות", ar: "يتذكر خيارات الموافقة على ملفات تعريف الارتباط", ru: "Сохраняет выбор согласия на cookie" } },
    ]
  },
  {
    categories: { en: "Functional", he: "פונקציונליים", ar: "وظيفية", ru: "Функциональные" },
    descriptions: { en: "Cookies that enhance functionality and personalization", he: "עוגיות המשפרות פונקציונליות והתאמה אישית", ar: "ملفات تعريف الارتباط التي تعزز الوظائف والتخصيص", ru: "Файлы cookie для улучшения функциональности" },
    cookies: [
      { name: "aac-dictionary", purposes: { en: "Stores learned words and phrases", he: "שמירת מילים וביטויים שנלמדו", ar: "يحفظ الكلمات والعبارات المكتسبة", ru: "Сохраняет выученные слова и фразы" } },
      { name: "communication-boards", purposes: { en: "Stores custom communication boards", he: "שמירת לוחות תקשורת מותאמים", ar: "يحفظ لوحات التواصل المخصصة", ru: "Сохраняет пользовательские доски общения" } },
      { name: "smart-home-settings", purposes: { en: "Stores smart home integration settings", he: "שמירת הגדרות בית חכם", ar: "يحفظ إعدادات المنزل الذكي", ru: "Сохраняет настройки умного дома" } },
    ]
  },
  {
    categories: { en: "Analytics", he: "אנליטיקה", ar: "تحليلات", ru: "Аналитика" },
    descriptions: { en: "Cookies used to understand how visitors use the app", he: "עוגיות לניתוח שימוש באפליקציה", ar: "ملفات تعريف الارتباط لفهم كيفية استخدام الزوار للتطبيق", ru: "Файлы cookie для анализа использования" },
    cookies: []
  }
];

const Legal = () => {
  const navigate = useNavigate();
  const [openSections, setOpenSections] = useState<string[]>([]);
  const { t, dir, isRTL, lang } = useTranslation();

  const localized = (map: Record<string, string>) => map[lang] || map.en || "";

  const legalLinks = [
    {
      id: "privacy",
      title: "Privacy Policy",
      localTitle: localized({ he: "מדיניות פרטיות", ar: "سياسة الخصوصية", ru: "Политика конфиденциальности", en: "Privacy Policy" }),
      icon: FileText,
      url: "/legal/privacy"
    },
    {
      id: "cookies",
      title: "Cookies Policy",
      localTitle: localized({ he: "מדיניות עוגיות", ar: "سياسة ملفات تعريف الارتباط", ru: "Политика cookie", en: "Cookies Policy" }),
      icon: Cookie,
      url: "/legal/cookies"
    },
    {
      id: "terms",
      title: "Terms and Conditions",
      localTitle: localized({ he: "תנאי שימוש", ar: "الشروط والأحكام", ru: "Условия использования", en: "Terms and Conditions" }),
      icon: Scale,
      url: "/legal/terms"
    },
    {
      id: "refund",
      title: "Refund Policy",
      localTitle: localized({ he: "מדיניות החזרים", ar: "سياسة الاسترداد", ru: "Политика возврата", en: "Refund Policy" }),
      icon: ReceiptText,
      url: "/legal/refund"
    }
  ];

  const toggleSection = (section: string) => {
    setOpenSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  return (
    <>
      <Helmet>
        <title>Legal - EyeControl App</title>
        <meta name="description" content="Legal information, privacy policy, terms and conditions, and open source attribution for EyeControl App AAC app." />
      </Helmet>

      <MainLayout>
        <div className="flex-1 flex flex-col h-full overflow-auto" dir={dir}>
          {/* Header */}
          <div className="flex items-center gap-4 p-6 border-b border-border">
            <h1 className="text-2xl font-bold text-foreground">{t("legalInfo")}</h1>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 space-y-6 max-w-4xl mx-auto w-full">
            {/* Legal Document Links */}
            <div className="grid gap-4 md:grid-cols-4 rounded-xl p-2">
              {legalLinks.map((link, idx) => (
                <div
                  key={link.id}
                  onClick={() => navigate(link.url)}
                  className="block cursor-pointer"
                  data-scan-item
                >
                  <Card className="h-full hover:border-primary transition-colors cursor-pointer">
                    <CardContent className="p-6 flex flex-col items-center text-center gap-4">
                      <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                        <link.icon className="w-7 h-7 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-foreground">{link.localTitle}</h3>
                        {lang !== "en" && <p className="text-sm text-muted-foreground">{link.title}</p>}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              ))}
            </div>

            {/* Collapsible Sections */}
            <div className="space-y-4 rounded-xl p-2">
              {/* Open Source Attribution */}
              <Collapsible
                open={openSections.includes('opensource')}
                onOpenChange={() => toggleSection('opensource')}
              >
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors" data-scan-item>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Code className="w-5 h-5 text-primary" />
                          </div>
                        <div>
                            <CardTitle className="text-lg">{t("openSource")}</CardTitle>
                            <p className="text-sm text-muted-foreground">{t("openSourceAttribution")}</p>
                          </div>
                        </div>
                        {openSections.includes('opensource') ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <p className="text-sm text-muted-foreground mb-4">
                        {t("openSourceDescription")}
                      </p>
                      <div className="space-y-2 max-h-80 overflow-y-auto">
                        {OPEN_SOURCE_LIBRARIES.map((lib, index) => (
                          <div 
                            key={index}
                            className="flex items-center justify-between p-3 rounded-lg bg-muted/30 hover:bg-muted/50 transition-colors"
                          >
                            <div className="flex items-center gap-3">
                              <a 
                                href={lib.url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="font-medium text-foreground hover:text-primary transition-colors"
                              >
                                {lib.name}
                              </a>
                              <span className="text-xs text-muted-foreground">v{lib.version}</span>
                            </div>
                            <span className="text-xs px-2 py-1 rounded bg-primary/10 text-primary">
                              {lib.license}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>

              {/* Cookies Information */}
              <Collapsible
                open={openSections.includes('cookies-info')}
                onOpenChange={() => toggleSection('cookies-info')}
              >
                <Card>
                  <CollapsibleTrigger asChild>
                    <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors" data-scan-item>
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <Cookie className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{t("cookiesInUse")}</CardTitle>
                            <p className="text-sm text-muted-foreground">{t("cookiesInUseSubtitle")}</p>
                          </div>
                        </div>
                        {openSections.includes('cookies-info') ? (
                          <ChevronUp className="w-5 h-5 text-muted-foreground" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-muted-foreground" />
                        )}
                      </div>
                    </CardHeader>
                  </CollapsibleTrigger>
                  <CollapsibleContent>
                    <CardContent className="pt-0 space-y-4">
                      {COOKIES_INFO.map((category, index) => (
                        <div key={index} className="space-y-2">
                          <div className="flex items-center gap-2">
                            <h4 className="font-semibold text-foreground">{localized(category.categories)}</h4>
                            {lang !== "en" && <span className="text-sm text-muted-foreground">({category.categories.en})</span>}
                          </div>
                          <p className="text-sm text-muted-foreground">{localized(category.descriptions)}</p>
                          {category.cookies.length > 0 ? (
                            <div className="space-y-1">
                              {category.cookies.map((cookie, cIndex) => (
                                <div 
                                  key={cIndex}
                                  className="flex items-center justify-between p-2 rounded bg-muted/30 text-sm"
                                >
                                  <code className="text-xs bg-background px-2 py-1 rounded">{cookie.name}</code>
                                  <span className="text-muted-foreground">{localized(cookie.purposes)}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-sm text-muted-foreground italic">{t("notInUse")}</p>
                          )}
                        </div>
                      ))}
                    </CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            </div>
          </div>
        </div>
      </MainLayout>
    </>
  );
};

export default Legal;
